import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ExperienceSectionProps {
  className?: string;
}

interface Experience {
  title: string;
  company: string;
  contractor?: string;
  period: string;
  location: string;
  skills: string[];
  achievements: string[];
  impact: {
    metric: string;
    description: string;
  }[];
}

const ExperienceSection: React.FC<ExperienceSectionProps> = ({ className }) => {
  const experiences: Experience[] = [
    {
      title: 'Senior Software Engineer',
      company: 'Intuit',
      period: '2024 – Present',
      location: 'San Diego, CA',
      skills: ['Java', 'Spring Boot', 'Kotlin', 'GraphQL', 'DynamoDB', 'AWS'],
      achievements: [
        "Design and develop scalable solutions for Intuit's Identity service, powering millions of customers and handling 100k+ transactions per second.",
        "Lead technical initiatives within the Identity team, driving innovation, reliability, and performance excellence for Intuit's core customer-facing services.",
        "Architect high-performance microservices that ensure seamless authentication and authorization across Intuit's ecosystem of financial products.",
      ],
      impact: [
        { metric: '100k+', description: 'Transactions per second' },
        { metric: 'Millions', description: 'Customers served' },
        { metric: '99.9%', description: 'Service uptime' },
        { metric: 'Enterprise', description: 'Scale solutions' },
      ],
    },
    {
      title: 'Senior Software Engineer',
      company: 'Apple',
      period: '2022 – 2024',
      location: 'San Diego, CA',
      skills: [
        'Java',
        'Spring Boot',
        'React.js',
        'TypeScript',
        'Python',
        'Airflow',
        'PostgreSQL',
        'AWS',
        'Kotlin',
        'GraphQL',
        'DynamoDB',
      ],
      achievements: [
        'Designed and implemented a scalable data pipeline and AI-powered application that processes billions of data points from factory operations, enabling automated root cause analysis to identify production inefficiencies and increase hardware output.',
        'Mentored and trained a team of software engineers, fostering their professional growth and technical skills in both front-end (React.js/TypeScript) and back-end (Java/Spring Boot) technologies.',
        'Built robust data processing systems that improved manufacturing efficiency and reduced production costs across multiple product lines.',
      ],
      impact: [
        { metric: 'Billions', description: 'Data points processed' },
        { metric: '5+', description: 'Engineers mentored' },
        { metric: '25%', description: 'Efficiency improvement' },
        { metric: 'AI-Powered', description: 'Root cause analysis' },
      ],
    },
    {
      title: 'Full Stack Software Engineer',
      company: 'PlayStation',
      period: '2021 – 2022',
      location: 'Los Angeles, CA',
      skills: ['Java', 'Spring Boot', 'React.js', 'TypeScript', 'AWS'],
      achievements: [
        'Led migration to Redux Toolkit, enhancing state management, code organization, and development workflows.',
        'Implemented new React design system, boosting code delivery speed and scalability.',
        'Collaborated on a cross-functional team at Sony, delivering new features 3 months ahead of schedule through effective project management and teamwork.',
      ],
      impact: [
        { metric: '3 months', description: 'Ahead of schedule' },
        { metric: '100%', description: 'Redux migration success' },
        { metric: '40%', description: 'Code delivery improvement' },
      ],
    },
    {
      title: 'Software Engineer',
      company: 'National Science Foundation',
      contractor: 'Jacobs',
      period: '2020 – 2021',
      location: 'Arlington, VA',
      skills: ['Java', 'Spring Boot', 'React.js', 'TypeScript', 'AWS'],
      achievements: [
        'Recognized as a top contributor, instrumental in delivering project milestones ahead of schedule.',
        'Demonstrated exceptional dedication, technical expertise, and collaborative skills, exceeding expectations and showcasing a commitment to excellence.',
        'Built enterprise-scale government applications with focus on performance and security compliance.',
      ],
      impact: [
        { metric: '1 year', description: 'Rapid impact' },
        { metric: 'Top', description: 'Contributor recognition' },
        { metric: '100%', description: 'Milestone delivery rate' },
        { metric: 'Security', description: 'Compliance focus' },
      ],
    },
    {
      title: 'Software Engineer',
      company: 'Calvary Chapel',
      period: '2016 – 2020',
      location: 'Temecula, CA',
      skills: ['Java', 'Spring Boot', 'React.js', 'JavaScript', 'MySQL', 'AWS'],
      achievements: [
        "Built and maintained the church's comprehensive website, serving thousands of community members with event information, sermon archives, and online giving capabilities.",
        'Managed and optimized the entire IT department infrastructure, ensuring reliable technology services for staff and congregation.',
        'Developed custom content management systems and integrated third-party services to enhance user experience and operational efficiency.',
      ],
      impact: [
        { metric: '4 years', description: 'IT leadership' },
        { metric: 'Thousands', description: 'Users served' },
        { metric: '100%', description: 'System uptime' },
        { metric: 'Full-Stack', description: 'Development' },
      ],
    },
    {
      title: 'Software Engineer',
      company: 'Hawk Analytics',
      period: '2015 – 2016',
      location: 'Temecula, CA',
      skills: ['Java', 'Spring Boot', 'JavaScript', 'PostgreSQL'],
      achievements: [
        'Built core features for call detail record location mapping software, enabling telecommunications companies to visualize and analyze network usage patterns.',
        "Collaborated with cross-functional teams to deliver critical features that enhanced the product's competitive positioning in the telecom analytics market.",
      ],
      impact: [
        { metric: '1 year', description: 'Foundation building' },
        { metric: 'Core', description: 'Features delivered' },
        { metric: 'Telecom', description: 'Industry expertise' },
      ],
    },
  ];

  return (
    <section id='experience' className={cn('py-20 bg-muted/30', className)}>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl sm:text-4xl font-bold text-foreground mb-4'>
            Professional Experience
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            A journey of growth from Software Engineer to Senior Engineer,
            delivering impact at scale across leading technology companies
          </p>
        </div>

        {/* Timeline */}
        <div className='relative'>
          {/* Timeline Line */}
          <div className='absolute left-4 md:left-1/2 md:transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-border'></div>

          {/* Experience Cards */}
          <div className='space-y-12'>
            {experiences.map((experience, index) => (
              <div
                key={index}
                className={cn(
                  'relative flex items-center',
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                )}
              >
                {/* Timeline Dot */}
                <div className='absolute left-4 md:left-1/2 md:transform md:-translate-x-1/2 w-4 h-4 bg-primary rounded-full border-4 border-background z-10'></div>

                {/* Content */}
                <div
                  className={cn(
                    'ml-12 md:ml-0 w-full',
                    index % 2 === 0 ? 'md:pr-8 md:w-1/2' : 'md:pl-8 md:w-1/2'
                  )}
                >
                  <Card className='hover:shadow-lg transition-shadow duration-300'>
                    <CardHeader>
                      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2'>
                        <CardTitle className='text-xl text-primary'>
                          {experience.title}
                        </CardTitle>
                        <Badge variant='outline' className='text-sm w-fit'>
                          {experience.period}
                        </Badge>
                      </div>
                      <div className='space-y-1'>
                        <p className='text-lg font-semibold text-foreground'>
                          {experience.company}
                          {experience.contractor && (
                            <span className='text-muted-foreground font-normal'>
                              {' '}
                              via {experience.contractor}
                            </span>
                          )}
                        </p>
                        <p className='text-sm text-muted-foreground'>
                          {experience.location}
                        </p>
                      </div>
                    </CardHeader>
                    <CardContent className='space-y-6'>
                      {/* Impact Metrics */}
                      <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
                        {experience.impact.map((item, impactIndex) => (
                          <div key={impactIndex} className='text-center'>
                            <div className='text-lg font-bold text-primary'>
                              {item.metric}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              {item.description}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Achievements */}
                      <div>
                        <h4 className='text-sm font-semibold text-foreground mb-3'>
                          Key Achievements
                        </h4>
                        <ul className='space-y-2'>
                          {experience.achievements.map(
                            (achievement, achievementIndex) => (
                              <li
                                key={achievementIndex}
                                className='flex items-start gap-2'
                              >
                                <span className='text-primary mt-1.5 text-xs'>
                                  ▸
                                </span>
                                <span className='text-sm text-muted-foreground leading-relaxed'>
                                  {achievement}
                                </span>
                              </li>
                            )
                          )}
                        </ul>
                      </div>

                      {/* Skills */}
                      <div>
                        <h4 className='text-sm font-semibold text-foreground mb-3'>
                          Technologies Used
                        </h4>
                        <div className='flex flex-wrap gap-2'>
                          {experience.skills.map(skill => (
                            <Badge
                              key={skill}
                              variant='secondary'
                              className='text-xs'
                            >
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Career Summary */}
        <div className='mt-16 text-center'>
          <h3 className='text-2xl font-semibold text-foreground mb-6'>
            Career Highlights
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto'>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>9+</div>
              <div className='text-sm text-muted-foreground'>
                Years of Experience
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>5</div>
              <div className='text-sm text-muted-foreground'>
                Companies & Organizations
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>100%</div>
              <div className='text-sm text-muted-foreground'>
                Project Success Rate
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
